import { useEffect, useMemo, useState } from "react";
import { Card, Select } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const ALGORITHMS = ["SHA-1", "SHA-256", "SHA-384", "SHA-512"];
const KEY_FORMATS = ["text", "hex", "base64"];
const OUTPUT_FORMATS = ["hex", "base64", "base64url"];

const DEFAULT_KEY = "my-shared-secret";
const DEFAULT_MESSAGE = "hello world";

const hexToBytes = (hex) => {
  const clean = (hex || "").replace(/\s+/g, "").replace(/^0x/i, "");
  if (clean.length === 0) return new Uint8Array(0);
  if (clean.length % 2 !== 0) throw new Error("odd-length hex");
  if (!/^[0-9a-fA-F]+$/.test(clean)) throw new Error("non-hex character");
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
};

const base64ToBytes = (b64) => {
  let s = (b64 || "").replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4;
  if (pad === 2) s += "==";
  else if (pad === 3) s += "=";
  else if (pad === 1) throw new Error("invalid base64 length");
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(s)) throw new Error("non-base64 character");
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
};

const bytesToHex = (bytes) =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const bytesToBase64 = (bytes) => {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};

const bytesToBase64Url = (bytes) =>
  bytesToBase64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");

const encodeOutput = (bytes, fmt) => {
  if (fmt === "hex") return bytesToHex(bytes);
  if (fmt === "base64") return bytesToBase64(bytes);
  if (fmt === "base64url") return bytesToBase64Url(bytes);
  return bytesToHex(bytes);
};

const parseKey = (raw, fmt) => {
  if (fmt === "hex") return hexToBytes(raw);
  if (fmt === "base64") return base64ToBytes(raw);
  return new TextEncoder().encode(raw || "");
};

const constantTimeEqualBytes = (a, b) => {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a[i] ^ b[i];
  return diff === 0;
};

const decodeSignature = (raw, fmt) => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;
  try {
    if (fmt === "hex") return hexToBytes(trimmed);
    if (fmt === "base64") return base64ToBytes(trimmed);
    if (fmt === "base64url") {
      const padded = trimmed.replace(/-/g, "+").replace(/_/g, "/");
      return base64ToBytes(padded);
    }
  } catch {
    return null;
  }
  return null;
};

const computeHmac = async (keyBytes, msgBytes, algorithm) => {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "HMAC", hash: { name: algorithm } },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, msgBytes);
  return new Uint8Array(sig);
};

const HmacGenerator = () => {
  const { t } = useTranslation();
  const [algorithm, setAlgorithm] = useState("SHA-256");
  const [keyFormat, setKeyFormat] = useState("text");
  const [outputFormat, setOutputFormat] = useState("hex");
  const [keyInput, setKeyInput] = useState(DEFAULT_KEY);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [expected, setExpected] = useState("");
  const [signature, setSignature] = useState("");
  const [error, setError] = useState("");

  const trimmedExpected = expected.trim();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const keyBytes = parseKey(keyInput, keyFormat);
        const msgBytes = new TextEncoder().encode(message || "");
        const out = await computeHmac(keyBytes, msgBytes, algorithm);
        if (cancelled) return;
        setSignature(encodeOutput(out, outputFormat));
        setError("");
      } catch (err) {
        if (cancelled) return;
        setSignature("");
        setError(err?.message || "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [keyInput, keyFormat, message, algorithm, outputFormat]);

  const verifyState = useMemo(() => {
    if (!trimmedExpected) return "idle";
    if (!signature) return "idle";
    const expectedBytes = decodeSignature(trimmedExpected, outputFormat);
    if (!expectedBytes) return "invalid";
    const signatureBytes = decodeSignature(signature, outputFormat);
    if (!signatureBytes) return "mismatch";
    return constantTimeEqualBytes(expectedBytes, signatureBytes) ? "match" : "mismatch";
  }, [trimmedExpected, signature, outputFormat]);

  const errorLabel = useMemo(() => {
    if (!error) return "";
    if (keyFormat === "hex") return t("hmac_error_invalid_hex");
    if (keyFormat === "base64") return t("hmac_error_invalid_base64");
    return t("hmac_error_generic");
  }, [error, keyFormat, t]);

  const copy = async () => {
    if (!signature) {
      toast.error(t("hmac_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(signature);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => {
    setKeyInput("");
    setMessage("");
    setExpected("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("hmac_title")}
            </h1>
            <p className="text-gray-600">{t("hmac_intro")}</p>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("hmac_algorithm_label")}
                </label>
                <Select
                  value={algorithm}
                  onChange={setAlgorithm}
                  options={ALGORITHMS.map((a) => ({ value: a, label: a }))}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("hmac_key_format_label")}
                </label>
                <Select
                  value={keyFormat}
                  onChange={setKeyFormat}
                  options={KEY_FORMATS.map((f) => ({
                    value: f,
                    label: t(`hmac_key_format_${f}`),
                  }))}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("hmac_output_format_label")}
                </label>
                <Select
                  value={outputFormat}
                  onChange={setOutputFormat}
                  options={OUTPUT_FORMATS.map((f) => ({
                    value: f,
                    label: t(`hmac_output_format_${f}`),
                  }))}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("hmac_key_label")}
              </label>
              <textarea
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder={t(`hmac_key_placeholder_${keyFormat}`)}
                rows={2}
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("hmac_message_label")}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("hmac_message_placeholder")}
                rows={5}
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
              <span className="text-xs text-gray-500">
                {t("hmac_message_hint")}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("hmac_signature_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!keyInput && !message && !expected}
                  >
                    {t("hmac_clear")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!signature}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              <pre className="w-full min-h-[3rem] p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap break-all text-gray-800">
                {error ? (
                  <em className="text-red-600 not-italic">{errorLabel}</em>
                ) : signature ? (
                  signature
                ) : (
                  <em className="text-gray-400 not-italic">
                    {t("hmac_output_empty")}
                  </em>
                )}
              </pre>
              {signature && !error && (
                <div className="text-xs text-gray-500">
                  {t("hmac_signature_length", { length: signature.length })}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("hmac_verify_label")}
              </label>
              <textarea
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                placeholder={t("hmac_verify_placeholder")}
                rows={2}
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
              {verifyState === "match" && (
                <div className="text-sm font-medium text-emerald-700">
                  {t("hmac_verify_match")}
                </div>
              )}
              {verifyState === "mismatch" && (
                <div className="text-sm font-medium text-red-600">
                  {t("hmac_verify_mismatch")}
                </div>
              )}
              {verifyState === "invalid" && (
                <div className="text-sm font-medium text-amber-600">
                  {t("hmac_verify_invalid", { format: t(`hmac_output_format_${outputFormat}`) })}
                </div>
              )}
              {verifyState === "idle" && (
                <span className="text-xs text-gray-500">
                  {t("hmac_verify_hint")}
                </span>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("hmac_note_webhook")}</p>
              <p>{t("hmac_note_compare")}</p>
              <p>{t("hmac_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default HmacGenerator;
